import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CreditCard, X } from "lucide-react";

/**
 * Dashboard üstünde koşullu tek satırlık abonelik uyarı şeridi.
 * Yalnızca gerektiğinde gösterilir:
 *  - trial: her zaman (kalan gün + Şimdi Abone Ol)
 *  - aylık paid: dönem bitimine ≤3 gün kaldığında
 *  - yıllık paid: dönem bitimine ≤15 gün kaldığında
 *  - cancel_at_period_end: her zaman (bitiş tarihiyle)
 *  - payment_pending: her zaman (tolerans mesajı + Ödemeyi Tamamla)
 *  - frozen: her zaman (Ödeme Yap)
 * internal_free ve normal aralıktaki paid'de gösterilmez.
 */
export default function SubscriptionBanner({ subtle = false }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/subscription");
        if (!cancelled) setSub(data);
      } catch { /* sessizce yut */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!sub || dismissed) return null;
  const eff = sub.effective_status;

  let content = null;
  let tone = "amber";
  let cta = null;

  if (eff === "trial") {
    const days = sub.trial_days_left ?? 0;
    const plan = sub.plan ? (sub.plan === "monthly" ? "Aylık" : "Yıllık") : null;
    content = (
      <>
        Ücretsiz denemenizin bitmesine <strong>{days} gün</strong> kaldı
        {plan ? <>. Seçtiğiniz plan: <strong>{plan}</strong>.</> : "."}
      </>
    );
    cta = { label: "Şimdi Abone Ol", onClick: () => navigate("/abonelik") };
  } else if (eff === "cancel_at_period_end") {
    content = (
      <>
        Aboneliğiniz <strong>{fmtDate(sub.subscription_end_date)}</strong> tarihinde sona erecektir. Bu tarihe kadar kullanmaya devam edebilirsiniz.
      </>
    );
  } else if (eff === "payment_pending") {
    const days = sub.grace_days_left ?? 0;
    content = (
      <>
        Ödemeniz alınamadı. Aboneliğiniz <strong>{fmtDate(sub.grace_period_end)}</strong> tarihine kadar aktif kalacaktır ({days} gün kaldı). Bu tarihe kadar ödeme yapılmazsa hesabınız dondurulacaktır.
      </>
    );
    cta = { label: "Ödemeyi Tamamla", onClick: () => navigate("/abonelik") };
  } else if (eff === "frozen") {
    tone = "rose";
    content = (
      <>Ödeme tamamlanmadığı için aboneliğiniz donduruldu. Penz&apos;i kullanmaya devam etmek için ödemenizi tamamlayın.</>
    );
    cta = { label: "Ödeme Yap", onClick: () => navigate("/abonelik") };
  } else if (eff === "paid") {
    const plan = sub.plan;
    const days = sub.days_left;
    if (plan === "monthly" && days !== null && days !== undefined && days <= 3) {
      content = <>Aylık abonelik döneminizin sona ermesine <strong>{days} gün</strong> kaldı.</>;
    } else if (plan === "yearly" && days !== null && days !== undefined && days <= 15) {
      content = <>Yıllık abonelik döneminizin sona ermesine <strong>{days} gün</strong> kaldı.</>;
    }
  }

  if (!content) return null;

  const toneCls = tone === "rose"
    ? "bg-rose-50 border-rose-200 text-rose-900"
    : "bg-amber-50 border-amber-200 text-amber-900";

  return (
    <div
      className={`flex items-start sm:items-center gap-3 px-4 py-3 rounded-xl border ${toneCls}`}
      data-testid="subscription-banner"
      role="status"
    >
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 sm:mt-0" />
      <div className="flex-1 text-sm leading-relaxed">{content}</div>
      {cta && (
        <Button
          size="sm"
          onClick={cta.onClick}
          variant={subtle && eff === "trial" ? "outline" : "default"}
          className={subtle && eff === "trial"
            ? "shrink-0 bg-transparent border-amber-300 text-amber-900 hover:bg-amber-100"
            : "bg-slate-900 hover:bg-slate-800 text-white shrink-0"}
          data-testid="subscription-banner-cta"
        >
          <CreditCard className="w-3.5 h-3.5 mr-1.5" /> {cta.label}
        </Button>
      )}
      {eff === "paid" && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-slate-500 hover:text-slate-900 shrink-0"
          aria-label="Kapat"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
}
